/**
 * InlineTranscriptPanel — right column live transcript.
 *
 * Distinct from the old fixed-positioned TranscriptPanel: this one is a
 * plain flow component so the new layout can compose it into a right
 * column alongside MemoryResonancePanel.
 *
 * Layout / overflow contract (verified at 1920×1080 AND 1440×820):
 *   • The <section> takes exactly the height its parent grants — the
 *     right column parent wraps this in `flex-1 min-h-0`. We add
 *     `h-full min-h-0` on the section so nested flex children can
 *     honour their overflow-y-auto without spilling.
 *   • The header is fixed height; the scroll region is `flex-1 min-h-0
 *     overflow-y-auto`. This is the standard flexbox overflow trap fix
 *     (min-h-0 lets the child shrink below its content size so the
 *     `overflow-y-auto` actually clips + scrolls).
 *
 * Preserves:
 *   • raw preservation (normalized displayed; raw available on toggle)
 *   • per-turn correction pill
 *   • YOU / QORYN role labels (cyan on QORYN, neutral on YOU)
 *
 * Auto-scroll behavior:
 *   • On every new turn or turn text change, if the user is within 48px
 *     of the bottom → auto-scroll to newest.
 *   • If the user has manually scrolled UP to read history, DO NOT yank
 *     them down. A "Latest ↓" pill appears offering to jump back.
 *   • Scroll-to-bottom button always available in the header.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Copy, Check, Download, ChevronsLeft, ChevronsRight } from "lucide-react";
import {
    copyTranscriptMarkdown,
    downloadTranscriptMarkdown,
} from "@/lib/transcriptExport";

const NEAR_BOTTOM_PX = 48;

// Cross-platform modifier label for tooltip hints. macOS/iPad shows ⌘
// so users recognize the key; Windows/Linux shows "Ctrl".
const SHORTCUT_MOD =
    typeof navigator !== "undefined" &&
    /Mac|iPad|iPhone|iPod/i.test(navigator.platform || navigator.userAgent || "")
        ? "⌘"
        : "Ctrl";

function TurnBubble({ turn }) {
    const [showRaw, setShowRaw] = useState(false);
    const hasCorrections = (turn.corrections?.length ?? 0) > 0;
    const isUser = turn.role === "user";
    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 90, damping: 22 }}
            data-testid={`turn-${turn.role}`}
            data-turn-id={turn.id}
            data-has-corrections={hasCorrections ? "true" : "false"}
            className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}
        >
            <span
                className={`font-mono-ui text-[9px] tracking-widest uppercase mb-1 ${
                    isUser ? "text-qoryn-textMuted" : "text-qoryn-cyan/80"
                }`}
            >
                {isUser ? "You" : "Qoryn"}
            </span>
            <p
                data-testid={`turn-${turn.role}-text`}
                className={`font-sans text-[13px] leading-relaxed max-w-[92%] ${
                    isUser
                        ? "text-qoryn-textDim text-right"
                        : "text-qoryn-text"
                } ${turn.status === "in_progress" ? "opacity-70" : ""}`}
            >
                {showRaw ? turn.raw : turn.normalized}
            </p>
            {hasCorrections && (
                <button
                    data-testid={`turn-${turn.role}-raw-toggle`}
                    onClick={() => setShowRaw((v) => !v)}
                    className="mt-1 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted hover:text-qoryn-textDim transition-colors duration-150 qoryn-focus"
                    aria-pressed={showRaw}
                >
                    {showRaw
                        ? "show normalized"
                        : `show raw · ${turn.corrections.length} fix${turn.corrections.length === 1 ? "" : "es"}`}
                </button>
            )}
        </motion.div>
    );
}

export function InlineTranscriptPanel({ turns = [], sessionStartedAt = null, expanded = true, onToggleExpanded }) {
    const scrollRef = useRef(null);
    // Tracks whether the user is "pinned" to the bottom (auto-scroll on)
    // vs having manually scrolled up (auto-scroll off). Default: pinned.
    const [pinned, setPinned] = useState(true);
    const pinnedRef = useRef(pinned);
    useEffect(() => { pinnedRef.current = pinned; }, [pinned]);

    // Copy-to-clipboard feedback: 'idle' → 'copied' → back to 'idle' after
    // 1.4s. Uses the shared transcriptExport helper.
    const [copyState, setCopyState] = useState("idle");
    const copyMarkdown = useCallback(async () => {
        if (turns.length === 0) return;
        const ok = await copyTranscriptMarkdown(turns, sessionStartedAt);
        setCopyState(ok ? "copied" : "error");
        setTimeout(() => setCopyState("idle"), 1400);
    }, [turns, sessionStartedAt]);

    const downloadMarkdown = useCallback(() => {
        if (turns.length === 0) return;
        downloadTranscriptMarkdown(turns, sessionStartedAt);
    }, [turns, sessionStartedAt]);

    // ---------------------------------------------------------------------
    // Keyboard shortcuts (cross-platform: metaKey on macOS, ctrlKey on
    // Windows/Linux). Attached at document level so they work whenever the
    // console has focus, but with clean guards so we NEVER hijack:
    //   • Cmd/Ctrl+C  → copyMarkdown, ONLY if there is no active text
    //     selection (Selection.toString().trim() === '') AND the event
    //     target is not inside an input / textarea / contenteditable.
    //     Regular text-copy behaviour is preserved verbatim.
    //   • Cmd/Ctrl+S  → downloadMarkdown, preventDefault on the browser's
    //     Save-Page dialog. Only fires if the transcript is non-empty and
    //     the event target is not inside an input.
    // ---------------------------------------------------------------------
    useEffect(() => {
        const isEditable = (el) => {
            if (!el) return false;
            const t = el.tagName;
            if (t === "INPUT" || t === "TEXTAREA" || t === "SELECT") return true;
            return el.isContentEditable === true;
        };
        const onKeyDown = (e) => {
            const mod = e.metaKey || e.ctrlKey;
            if (!mod) return;
            // Skip when typing in the text-input drawer, etc.
            if (isEditable(e.target)) return;
            const key = e.key.toLowerCase();
            if (key === "c") {
                // Preserve normal text-copy: only intercept when there's
                // no active selection.
                const sel = window.getSelection?.()?.toString?.() ?? "";
                if (sel.trim().length > 0) return;
                if (turns.length === 0) return;
                e.preventDefault();
                copyMarkdown();
            } else if (key === "s") {
                if (turns.length === 0) return;
                e.preventDefault();
                downloadMarkdown();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [turns.length, copyMarkdown, downloadMarkdown]);

    const scrollToBottom = useCallback((behavior = "auto") => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior });
    }, []);

    // Only auto-scroll when the user is pinned to the bottom. This uses
    // useLayoutEffect so scroll runs BEFORE paint — no visible jump.
    // Depends on both count and the last turn's text length so streaming
    // deltas keep the tail visible.
    const lastText = turns.length > 0 ? (turns[turns.length - 1].normalized ?? "") : "";
    useLayoutEffect(() => {
        if (pinnedRef.current) {
            scrollToBottom("auto");
        }
    }, [turns.length, lastText, scrollToBottom]);

    // Detect manual scroll — update `pinned` based on distance from
    // bottom. This runs on every scroll event; state only changes when
    // crossing the threshold so React re-renders are minimal.
    const onScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
        const nowPinned = distance <= NEAR_BOTTOM_PX;
        if (nowPinned !== pinnedRef.current) {
            pinnedRef.current = nowPinned;
            setPinned(nowPinned);
        }
    }, []);

    return (
        <section
            data-testid="transcript-panel"
            className="qoryn-panel rounded-2xl flex flex-col overflow-hidden h-full min-h-0 relative"
            style={{ borderColor: "rgba(0,229,255,0.09)" }}
        >
            <header className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-white/[0.04] shrink-0">
                <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-qoryn-cyan" />
                    <span className="font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim">
                        Transcript
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    {onToggleExpanded && (
                        <button
                            data-testid="transcript-expand-toggle"
                            aria-label={expanded ? "Collapse transcript column" : "Expand transcript column"}
                            aria-pressed={expanded}
                            title={expanded ? "Collapse transcript" : "Expand transcript"}
                            onClick={onToggleExpanded}
                            className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim hover:text-qoryn-text transition-colors duration-150 qoryn-focus flex items-center"
                        >
                            {expanded
                                ? <ChevronsRight className="h-3 w-3" strokeWidth={1.75} />
                                : <ChevronsLeft className="h-3 w-3" strokeWidth={1.75} />}
                        </button>
                    )}
                    <button
                        data-testid="transcript-copy-btn"
                        aria-label="Copy transcript as markdown"
                        title={turns.length === 0
                            ? "Nothing to copy yet"
                            : `Copy transcript as Markdown  (${SHORTCUT_MOD}+C)`}
                        disabled={turns.length === 0}
                        onClick={copyMarkdown}
                        className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim hover:text-qoryn-text disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150 qoryn-focus flex items-center gap-1"
                    >
                        {copyState === "copied" ? (
                            <Check className="h-3 w-3 text-qoryn-cyan" strokeWidth={2} />
                        ) : (
                            <Copy className="h-3 w-3" strokeWidth={1.75} />
                        )}
                        {copyState === "copied" ? "Copied" : "Copy"}
                    </button>
                    <button
                        data-testid="transcript-download-btn"
                        aria-label="Download transcript as .md"
                        title={turns.length === 0
                            ? "Nothing to download yet"
                            : `Download transcript as .md  (${SHORTCUT_MOD}+S)`}
                        disabled={turns.length === 0}
                        onClick={downloadMarkdown}
                        className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim hover:text-qoryn-text disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150 qoryn-focus flex items-center gap-1"
                    >
                        <Download className="h-3 w-3" strokeWidth={1.75} />
                        .md
                    </button>
                    <button
                        data-testid="transcript-scroll-bottom-btn"
                        aria-label="Scroll to newest"
                        onClick={() => {
                            setPinned(true);
                            pinnedRef.current = true;
                            scrollToBottom("smooth");
                        }}
                        className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim hover:text-qoryn-text transition-colors duration-150 qoryn-focus flex items-center gap-1"
                    >
                        <ChevronDown className="h-3 w-3" strokeWidth={1.75} />
                        Latest
                    </button>
                </div>
            </header>
            <div
                ref={scrollRef}
                data-testid="transcript-scroll"
                data-pinned={pinned ? "true" : "false"}
                onScroll={onScroll}
                className="qoryn-transcript-scroll flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4"
            >
                {turns.length === 0 && (
                    <div className="pt-6 text-qoryn-textMuted text-sm font-sans">
                        Nothing yet. Speak or send text to begin.
                    </div>
                )}
                <AnimatePresence initial={false}>
                    {turns.map((t) => (
                        <TurnBubble key={t.id} turn={t} />
                    ))}
                </AnimatePresence>
            </div>
            {/* Sticky "Latest ↓" pill when user has scrolled up. */}
            {!pinned && turns.length > 0 && (
                <button
                    data-testid="transcript-jump-latest"
                    onClick={() => {
                        setPinned(true);
                        pinnedRef.current = true;
                        scrollToBottom("smooth");
                    }}
                    className="absolute bottom-3 left-1/2 -translate-x-1/2 qoryn-panel rounded-full px-3 py-1 flex items-center gap-1 hover:text-qoryn-text transition-colors duration-150 qoryn-focus"
                    style={{ borderColor: "rgba(0,229,255,0.20)" }}
                >
                    <ChevronDown className="h-3 w-3 text-qoryn-cyan" strokeWidth={2} />
                    <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                        Jump to latest
                    </span>
                </button>
            )}
        </section>
    );
}

export default InlineTranscriptPanel;
